import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export type ConnectionQuality = 'high' | 'medium' | 'low' | 'unknown';

/**
 * Hook para monitorar a conectividade de rede e a qualidade da conexão
 * @returns Objeto com informações sobre o estado da conexão
 */
const useConnectivity = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>('unknown');
  const [latency, setLatency] = useState<number>(0);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Conexão restaurada! Retomando operação...");
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.error("Conexão perdida. Os dados serão salvos localmente até que a conexão seja restaurada.");
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Estimar qualidade da conexão com um pequeno ping
    const checkConnectionSpeed = async () => {
      if (!navigator.onLine) {
        setConnectionQuality('unknown');
        return;
      }

      try {
        // Usa um timestamp para evitar caching
        const timestamp = new Date().getTime();
        const start = Date.now();
        // Faz uma requisição pequena para medir a latência
        await fetch(`${window.location.origin}/favicon.ico?t=${timestamp}`, { 
          cache: 'no-store',
          method: 'HEAD'
        });
        const duration = Date.now() - start;
        setLatency(duration);
        
        if (duration < 150) setConnectionQuality('high');
        else if (duration < 500) setConnectionQuality('medium');
        else setConnectionQuality('low');
      } catch (e) {
        setConnectionQuality('unknown');
      }
    };
    
    // Verificar a qualidade da conexão inicialmente e a cada 30 segundos
    checkConnectionSpeed();
    const interval = setInterval(checkConnectionSpeed, 30000);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);
  
  return { 
    isOnline, 
    connectionQuality, 
    latency,
    // Método para verificar se a conexão é adequada para uploads
    isConnectionSuitable: () => isOnline && connectionQuality !== 'low'
  };
};

export default useConnectivity; 